import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchType } from 'fym-common/src/types/searchService';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { useTrackDetails } from '../../../music24/hooks/useTrackDetails';
import { useLazySearchQuery } from '../../../../redux/api/musicAPI';
import { ReleaseLinkData } from '../releaseLink/types';
import { parseCatalogTrackToReleaseLinks, parseSearchResults } from '../releaseLink/utils';
import { ReleaseResolution } from './releaseResolutionTypes';
import { toReleaseDay } from './toReleaseDay';

/** Every source a found link belongs to, in the order they were found. */
function sourcesOf(links: ReleaseLinkData[]): MusicAPIIntegrationID[] {
  return links.reduce<MusicAPIIntegrationID[]>((sources, link) => {
    if (link.source !== undefined && !sources.includes(link.source)) {
      sources.push(link.source);
    }
    return sources;
  }, []);
}

/**
 * A catalog track turned into the links a release page needs.
 *
 * The catalog already holds some of them, so only the services it does not
 * cover are searched — the same bargain `CatalogTrackResolver` has always
 * struck, lifted out of it so the creation sheet can watch it happen rather
 * than wait behind a spinner.
 */
export function useCatalogTrackResolution(trackMaid: string | undefined): ReleaseResolution {
  const [search] = useLazySearchQuery();
  const {
    data: track,
    isLoading,
    error,
    refetch,
  } = useTrackDetails({ trackId: trackMaid ?? '', enabled: trackMaid !== undefined });

  // `undefined` while the search is still out; an array once it has answered,
  // empty included. That distinction is what "pending" is read from.
  const [searched, setSearched] = useState<ReleaseLinkData[]>();
  const startedForMaid = useRef<string | undefined>(undefined);

  const known = useMemo(() => (track ? parseCatalogTrackToReleaseLinks(track) : undefined), [track]);

  useEffect(() => {
    if (trackMaid === undefined || known === undefined || startedForMaid.current === trackMaid) {
      return;
    }
    startedForMaid.current = trackMaid;
    setSearched(undefined);

    if (known.missingSources.length === 0 || track === undefined) {
      setSearched([]);
      return;
    }
    search({
      track: track.mainName,
      album: track.mainAlbumName ?? undefined,
      artist: track.mainArtistName ?? undefined,
      sources: known.missingSources,
      type: SearchType.track,
    })
      .then((searchResult) => {
        if (!searchResult.isSuccess) {
          setSearched([]);
          return;
        }
        const { tracks, albums } = searchResult.data;
        setSearched(parseSearchResults([...(tracks ?? []), ...(albums ?? [])]));
      })
      .catch(() => setSearched([]));
  }, [known, search, track, trackMaid]);

  const retry = useCallback(() => {
    startedForMaid.current = undefined;
    refetch().catch(console.error);
  }, [refetch]);

  return useMemo(() => {
    const links = [...(known?.releaseLinks ?? []), ...(searched ?? [])];
    const [main, ...others] = links;
    const foundSources = sourcesOf(links);
    const isSearching = trackMaid !== undefined && searched === undefined;
    const isResolving = (isLoading && trackMaid !== undefined) || isSearching;

    return {
      isResolving,
      error: error ?? undefined,
      retry,
      main,
      others,
      foundSources,
      pendingSources: isSearching ? (known?.missingSources ?? []) : [],
      missingSources: isSearching ? [] : (known?.missingSources ?? []).filter((s) => !foundSources.includes(s)),
      releaseDate: toReleaseDay(track?.album?.releaseDate),
      title: track?.mainName ?? undefined,
      artistName: track?.mainArtistName ?? undefined,
      albumName: track?.mainAlbumName ?? undefined,
      imageUrl: track?.imageUrl ?? undefined,
      hasNoLinks: !isResolving && trackMaid !== undefined && links.length === 0,
    };
  }, [error, isLoading, known, retry, searched, track, trackMaid]);
}
